import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth, setKeepLoggedIn } from "@/App";
import { toast } from "sonner";
import { auth, googleProvider } from "@/firebase";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";

const LoginPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [keepLoggedIn, setKeep] = useState(() => {
    // Remember user's prior choice; default to true (persistent) on first visit.
    try { return localStorage.getItem("aura_keep_logged_in") !== "false"; } catch { return true; }
  });

  // Apply persistence preference both to Firebase and our auth storage.
  const applyPersistence = async (keep) => {
    setKeepLoggedIn(keep);
    try {
      await setPersistence(auth, keep ? browserLocalPersistence : browserSessionPersistence);
    } catch (err) {
      console.warn("[AURA Auth] setPersistence failed:", err?.code || err?.message);
    }
  };

  // Redirect if already logged in (uses effect to avoid setState-during-render warning)
  useEffect(() => {
    if (user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await applyPersistence(keepLoggedIn);
      await signInWithEmailAndPassword(auth, email, password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (error) {
      const code = error.code;
      if (code === "auth/user-not-found" || code === "auth/wrong-password" || code === "auth/invalid-credential") {
        toast.error("Invalid email or password");
      } else if (code === "auth/too-many-requests") {
        toast.error("Too many attempts. Please try again later.");
      } else {
        toast.error("Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      await applyPersistence(keepLoggedIn);
      console.log('[AURA Auth] Starting Google popup sign-in...');
      const result = await signInWithPopup(auth, googleProvider);
      console.log('[AURA Auth] Popup completed, user:', result.user.email);
      // onAuthStateChanged in AuthProvider will exchange token and set user state.
      // LoginPage auto-navigates to /dashboard when user is set (line 23-26).
    } catch (error) {
      console.error('[AURA Auth] Google popup error:', error.code, error.message);
      if (error.code === 'auth/popup-blocked') {
        toast.error('Popup blocked by browser. Please allow popups for this site.');
      } else if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
        toast.error('Google sign-in failed. Please try again.');
      }
      setGoogleLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast.error("Enter your email address first");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success("Password reset email sent! Check your inbox.");
    } catch (error) {
      if (error.code === "auth/user-not-found") {
        toast.error("No account found with that email");
      } else {
        toast.error("Failed to send reset email. Try again.");
      }
    }
  };

  return (
    <div className="min-h-screen bg-aura-bg flex items-center justify-center px-6 relative">
      {/* Background effects */}
      <div className="noise-overlay fixed inset-0 pointer-events-none" />
      <div className="grid-bg fixed inset-0 pointer-events-none opacity-30" />
      <div className="absolute inset-0 bg-aura-glow opacity-20" />

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-8" data-testid="login-logo">
          <svg width="48" height="48" viewBox="0 0 100 100" className="glow">
            <defs>
              <radialGradient id="ball-login" cx="30%" cy="30%">
                <stop offset="0%" stopColor="#444"/>
                <stop offset="100%" stopColor="#000"/>
              </radialGradient>
            </defs>
            <circle cx="50" cy="50" r="45" fill="url(#ball-login)" />
            <circle cx="50" cy="50" r="18" fill="white"/>
            <text x="50" y="58" textAnchor="middle" fontSize="22" fontWeight="bold" fill="black">8</text>
          </svg>
          <span className="font-heading text-3xl font-bold text-white">AURA</span>
        </Link>

        {/* Card */}
        <div className="card-aura p-8">
          <div className="text-center mb-8">
            <h1 className="font-heading text-2xl font-bold text-white mb-2">Welcome Back</h1>
            <p className="text-aura-text-secondary">Sign in to continue your journey</p>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full mb-6 border-white/20 hover:border-white/40 bg-white/5 text-white py-6"
            onClick={handleGoogleLogin}
            disabled={googleLoading}
            data-testid="google-login-btn"
          >
            {googleLoading ? (
              <Loader2 className="w-5 h-5 mr-3 animate-spin" />
            ) : (
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
            Continue with Google
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-aura-surface text-aura-text-muted">or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-aura-text-secondary">Email</Label>
              <div className="relative input-icon-wrapper">
                <Mail className="input-icon w-5 h-5" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="input-aura pl-11"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-aura-text-secondary">Password</Label>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-xs text-aura-emerald hover:text-aura-emerald-glow transition-colors"
                  data-testid="forgot-password-btn"
                >
                  Forgot Password?
                </button>
              </div>
              <div className="relative input-icon-wrapper">
                <Lock className="input-icon w-5 h-5" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-aura pl-11 pr-11"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-aura-text-muted hover:text-white transition-colors z-10"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <Checkbox
                id="keep-logged-in"
                checked={keepLoggedIn}
                onCheckedChange={(checked) => setKeep(checked === true)}
                className="border-white/30 data-[state=checked]:bg-aura-emerald data-[state=checked]:border-aura-emerald"
                data-testid="keep-logged-in-checkbox"
              />
              <Label
                htmlFor="keep-logged-in"
                className="text-sm text-aura-text-secondary cursor-pointer select-none"
              >
                Keep me logged in
              </Label>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary py-6"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-aura-text-secondary">
            Don't have an account?{" "}
            <Link to="/register" className="text-aura-emerald hover:text-aura-emerald-glow transition-colors" data-testid="register-link">
              Sign up free
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
